import React, { useState, useEffect } from 'react';
import { Icon, AppleIcon, AndroidIcon } from './icons';
import { HeroPhone, ScreenPhone, OffersScreen, BenefitsScreen, QRScreen } from './PhoneScreens';
import ApplyModal from './ApplyModal';
import { LanguageProvider, useLanguage } from './LanguageContext';
import { SUPPORTED_LANGUAGES } from './translations';
import logoWhite from '../../assets/logo_white.png';
import '../../styles/Marketing.css';

const LangSwitcher = ({ variant = 'pill' }) => {
  const { lang, setLang } = useLanguage();
  if (variant === 'drawer') {
    return (
      <div className="mkt-lang-drawer">
        <div className="mkt-lang-drawer-label">Language · ენა</div>
        <div className="mkt-lang-drawer-grid">
          {SUPPORTED_LANGUAGES.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => setLang(l.code)}
              className={`mkt-lang-drawer-btn${lang === l.code ? ' active' : ''}`}
              aria-label={l.name}
            >
              <span className="mkt-lang-drawer-code">{l.label}</span>
              <span className="mkt-lang-drawer-name">{l.name}</span>
              {lang === l.code && (
                <svg className="mkt-lang-drawer-check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="mkt-lang-switch">
      {SUPPORTED_LANGUAGES.map((l) => (
        <button
          key={l.code}
          type="button"
          onClick={() => setLang(l.code)}
          className={`mkt-lang-btn${lang === l.code ? ' active' : ''}`}
          aria-label={l.name}
          title={l.name}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
};

const Nav = () => {
  const { t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <nav className={`mkt-nav${scrolled ? ' scrolled' : ''}`}>
        <a href="/" aria-label="Loysta home"><img src={logoWhite} alt="Loysta" className="mkt-nav-logo" /></a>
        <div className="mkt-nav-right">
          <a href="#what-is" className="mkt-nav-link">{t.nav.product}</a>
          <a href="#how-it-works" className="mkt-nav-link">{t.nav.howItWorks}</a>
          <a href="#partner" className="mkt-nav-link">{t.nav.partner}</a>
          <LangSwitcher />
          <a href="#download" className="mkt-nav-cta">{t.nav.download}</a>
          <button
            className="mkt-nav-hamburger"
            onClick={() => setMenuOpen(o => !o)}
            aria-label="Menu"
            type="button"
          >
            <Icon name={menuOpen ? 'x' : 'menu'} size={22} color="white" />
          </button>
        </div>
      </nav>
      <div className={`mkt-mobile-menu${menuOpen ? ' open' : ''}`} onClick={() => setMenuOpen(false)}>
        <a href="#what-is">{t.nav.product}</a>
        <a href="#how-it-works">{t.nav.howItWorks}</a>
        <a href="#partner">{t.nav.partner}</a>
        <LangSwitcher variant="drawer" />
        <a href="#download" className="mkt-mobile-menu-cta">{t.nav.download}</a>
      </div>
    </>
  );
};

const Hero = () => {
  const { t } = useLanguage();
  return (
    <section className="mkt-hero">
      <div className="mkt-hero-blob1" />
      <div className="mkt-hero-blob2" />
      <div className="mkt-hero-grid">
        <div>
          <div className="mkt-hero-badge">
            <div className="mkt-hero-badge-dot" />
            {t.hero.badge}
          </div>
          <h1 className="mkt-hero-title">
            {t.hero.titleLine1}<br />
            <em>{t.hero.titleLine2}</em>
          </h1>
          <p className="mkt-hero-sub">{t.hero.sub}</p>
          <div className="mkt-hero-btns">
            <a href="#download" className="mkt-store-btn">
              <AppleIcon />
              <div>
                <span className="mkt-store-btn-label">{t.hero.storeApple}</span>
                <span className="mkt-store-btn-name">{t.hero.storeAppleName}</span>
              </div>
            </a>
            <a href="#download" className="mkt-store-btn">
              <AndroidIcon />
              <div>
                <span className="mkt-store-btn-label">{t.hero.storeAndroid}</span>
                <span className="mkt-store-btn-name">{t.hero.storeAndroidName}</span>
              </div>
            </a>
          </div>
          <div className="mkt-hero-trust">
            <span>{t.hero.trustFree}</span>
            <div className="mkt-hero-trust-dot" />
            <span>{t.hero.trustPlatforms}</span>
            <div className="mkt-hero-trust-dot" />
            <span>{t.hero.trustNoCard}</span>
          </div>
        </div>
        <div className="mkt-phone-stage"><HeroPhone /></div>
      </div>
    </section>
  );
};

const StatsBar = () => {
  const { t } = useLanguage();
  return (
    <div className="mkt-stats-bar">
      <div className="mkt-stats-grid">
        <div className="mkt-stat-item mkt-stat-divider"><div className="mkt-stat-value">500+</div><div className="mkt-stat-label">{t.stats.vendors}</div></div>
        <div className="mkt-stat-item mkt-stat-divider"><div className="mkt-stat-value">35K+</div><div className="mkt-stat-label">{t.stats.employees}</div></div>
        <div className="mkt-stat-item"><div className="mkt-stat-value">120+</div><div className="mkt-stat-label">{t.stats.companies}</div></div>
      </div>
    </div>
  );
};

const WhatIs = () => {
  const { t } = useLanguage();
  const pillIcons = [
    { icon: 'tag',      bg: '#dcfce7', ic: '#00bf63' },
    { icon: 'shield',   bg: '#dbeafe', ic: '#3b82f6' },
    { icon: 'qr',       bg: '#fef3c7', ic: '#f59e0b' },
    { icon: 'lock',     bg: '#ede9fe', ic: '#8b5cf6' },
  ];
  const userTypes = [
    { key: 'employees', icon: 'user',     bg: 'rgba(0,191,99,0.18)',  ic: '#00bf63' },
    { key: 'admins',    icon: 'building', bg: 'rgba(59,130,246,0.18)', ic: '#60a5fa' },
    { key: 'vendors',   icon: 'tag',      bg: 'rgba(245,158,11,0.18)', ic: '#fbbf24' },
  ];
  return (
    <section className="mkt-whatis" id="what-is">
      <div className="mkt-section-inner">
        <div className="mkt-whatis-grid">
          <div>
            <div className="mkt-section-label">{t.whatIs.label}</div>
            <h2 className="mkt-section-title">{t.whatIs.titleLine1}<br />{t.whatIs.titleLine2}</h2>
            <p className="mkt-section-body">{t.whatIs.body}</p>
            <div className="mkt-pills">
              {t.whatIs.pills.map((label, i) => (
                <div key={i} className="mkt-pill">
                  <div className="mkt-pill-icon" style={{ background: pillIcons[i].bg }}>
                    <Icon name={pillIcons[i].icon} size={20} color={pillIcons[i].ic} />
                  </div>
                  <div className="mkt-pill-text">{label}</div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="mkt-whatis-card">
              <div className="mkt-whatis-card-title">{t.whatIs.cardTitle}</div>
              <div className="mkt-whatis-card-sub">{t.whatIs.cardSub}</div>
              <div className="mkt-user-types">
                {userTypes.map((u) => {
                  const tx = t.whatIs.types[u.key];
                  return (
                    <div key={u.key} className="mkt-user-type">
                      <div className="mkt-user-type-icon" style={{ background: u.bg }}>
                        <Icon name={u.icon} size={18} color={u.ic} />
                      </div>
                      <div>
                        <div className="mkt-user-type-name">{tx.name}</div>
                        <div className="mkt-user-type-desc">{tx.desc}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Features = () => {
  const { t } = useLanguage();
  const styles = [
    { icon: 'gift', bg: '#dcfce7', ic: '#00bf63' },
    { icon: 'star', bg: '#fef3c7', ic: '#f59e0b' },
    { icon: 'qr',   bg: '#ede9fe', ic: '#8b5cf6' },
  ];
  return (
    <section className="mkt-features">
      <div className="mkt-section-inner">
        <div className="mkt-features-header">
          <div className="mkt-section-label">{t.features.label}</div>
          <h2 className="mkt-section-title">{t.features.title}</h2>
        </div>
        <div className="mkt-features-grid">
          {t.features.items.map((f, i) => (
            <div key={i} className="mkt-feature-card">
              <div className="mkt-feature-icon" style={{ background: styles[i].bg }}>
                <Icon name={styles[i].icon} size={26} color={styles[i].ic} />
              </div>
              <div className="mkt-feature-title">{f.title}</div>
              <div className="mkt-feature-body">{f.body}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const AppScreens = () => {
  const { t } = useLanguage();
  return (
    <section className="mkt-screens">
      <div className="mkt-section-inner">
        <div className="mkt-screens-header">
          <div className="mkt-section-label">{t.appScreens.label}</div>
          <h2 className="mkt-section-title">{t.appScreens.title}</h2>
        </div>
      </div>
      <div className="mkt-section-inner" style={{ position: 'relative' }}>
        <div className="mkt-screens-grid">
          <div className="mkt-screen-item">
            <ScreenPhone><OffersScreen /></ScreenPhone>
            <div className="mkt-screen-caption">
              <div className="mkt-screen-caption-title">{t.appScreens.browseTitle}</div>
              <div className="mkt-screen-caption-sub">{t.appScreens.browseSub}</div>
            </div>
          </div>
          <div className="mkt-screen-item mkt-screen-item-mid">
            <ScreenPhone><BenefitsScreen /></ScreenPhone>
            <div className="mkt-screen-caption">
              <div className="mkt-screen-caption-title">{t.appScreens.benefitsTitle}</div>
              <div className="mkt-screen-caption-sub">{t.appScreens.benefitsSub}</div>
            </div>
          </div>
          <div className="mkt-screen-item">
            <ScreenPhone><QRScreen /></ScreenPhone>
            <div className="mkt-screen-caption">
              <div className="mkt-screen-caption-title">{t.appScreens.redeemTitle}</div>
              <div className="mkt-screen-caption-sub">{t.appScreens.redeemSub}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const HowItWorks = () => {
  const { t } = useLanguage();
  return (
    <section className="mkt-hiw" id="how-it-works">
      <div className="mkt-section-inner">
        <div className="mkt-hiw-header">
          <div className="mkt-section-label" style={{ textAlign: 'center' }}>{t.howItWorks.label}</div>
          <h2 className="mkt-section-title" style={{ textAlign: 'center' }}>{t.howItWorks.title}</h2>
        </div>
        <div className="mkt-hiw-grid">
          {t.howItWorks.steps.map((s, i) => (
            <div key={i} className="mkt-hiw-step">
              <div className="mkt-hiw-num">{i + 1}</div>
              <div>
                <div className="mkt-hiw-title">{s.title}</div>
                <div className="mkt-hiw-body">{s.body}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Partner = ({ onApply }) => {
  const { t } = useLanguage();
  return (
    <section className="mkt-partner" id="partner">
      <div className="mkt-section-inner">
        <div className="mkt-partner-header">
          <div className="mkt-section-label" style={{ textAlign: 'center' }}>{t.partner.label}</div>
          <h2 className="mkt-section-title" style={{ textAlign: 'center' }}>{t.partner.title}</h2>
          <p className="mkt-partner-sub">{t.partner.sub}</p>
        </div>

        <div className="mkt-partner-grid">
          <div className="mkt-partner-card mkt-partner-card-company">
            <div className="mkt-partner-card-icon">
              <Icon name="building" size={26} color="#00bf63" />
            </div>
            <h3 className="mkt-partner-card-title">{t.partner.companyTitle}</h3>
            <p className="mkt-partner-card-body">{t.partner.companyBody}</p>
            <ul className="mkt-partner-card-list">
              {t.partner.companyBullets.map((b, i) => (
                <li key={i}><Icon name="check" size={14} color="#00bf63" /> {b}</li>
              ))}
            </ul>
            <button
              type="button"
              className="mkt-partner-card-btn mkt-partner-btn-company"
              onClick={() => onApply('company')}
            >
              {t.partner.companyCta}
              <Icon name="arrow-right" size={16} color="white" />
            </button>
          </div>

          <div className="mkt-partner-card mkt-partner-card-vendor">
            <div className="mkt-partner-card-icon mkt-partner-card-icon-vendor">
              <Icon name="store" size={26} color="#8b5cf6" />
            </div>
            <h3 className="mkt-partner-card-title">{t.partner.vendorTitle}</h3>
            <p className="mkt-partner-card-body">{t.partner.vendorBody}</p>
            <ul className="mkt-partner-card-list">
              {t.partner.vendorBullets.map((b, i) => (
                <li key={i}><Icon name="check" size={14} color="#8b5cf6" /> {b}</li>
              ))}
            </ul>
            <button
              type="button"
              className="mkt-partner-card-btn mkt-partner-btn-vendor"
              onClick={() => onApply('vendor')}
            >
              {t.partner.vendorCta}
              <Icon name="arrow-right" size={16} color="white" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

const Download = () => {
  const { t } = useLanguage();
  return (
    <section className="mkt-download" id="download">
      <div className="mkt-section-inner mkt-download-inner">
        <div className="mkt-section-label" style={{ textAlign: 'center', color: '#c1ff72', marginBottom: 16 }}>{t.download.label}</div>
        <h2 className="mkt-download-title">{t.download.titleLine1}<br />{t.download.titleLine2}</h2>
        <p className="mkt-download-sub">{t.download.sub}</p>
        <div className="mkt-download-btns">
          <a href="#" className="mkt-dl-btn" onClick={(e) => e.preventDefault()}>
            <AppleIcon />
            <div>
              <div className="mkt-dl-btn-label">{t.hero.storeApple}</div>
              <div className="mkt-dl-btn-name">{t.hero.storeAppleName}</div>
            </div>
          </a>
          <a href="#" className="mkt-dl-btn" onClick={(e) => e.preventDefault()}>
            <AndroidIcon />
            <div>
              <div className="mkt-dl-btn-label">{t.hero.storeAndroid}</div>
              <div className="mkt-dl-btn-name">{t.hero.storeAndroidName}</div>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  const { t } = useLanguage();
  return (
    <footer className="mkt-footer">
      <div className="mkt-footer-inner">
        <img src={logoWhite} alt="Loysta" className="mkt-footer-logo" />
        <div className="mkt-footer-copy">© {new Date().getFullYear()} Loysta. {t.footer.copy}</div>
        <div className="mkt-footer-links">
          <a href="#" className="mkt-footer-link" onClick={(e) => e.preventDefault()}>{t.footer.privacy}</a>
          <a href="#" className="mkt-footer-link" onClick={(e) => e.preventDefault()}>{t.footer.terms}</a>
          <a href="#" className="mkt-footer-link" onClick={(e) => e.preventDefault()}>{t.footer.contact}</a>
        </div>
      </div>
    </footer>
  );
};

const MarketingInner = () => {
  const [applyType, setApplyType] = useState(null);
  return (
    <div className="mkt-page">
      <Nav />
      <main>
        <Hero />
        <StatsBar />
        <WhatIs />
        <Features />
        <AppScreens />
        <HowItWorks />
        <Partner onApply={setApplyType} />
        <Download />
      </main>
      <Footer />
      {applyType && <ApplyModal type={applyType} onClose={() => setApplyType(null)} />}
    </div>
  );
};

const Marketing = () => (
  <LanguageProvider>
    <MarketingInner />
  </LanguageProvider>
);

export default Marketing;
